import React, { useRef, useState, useEffect } from "react";
import "../styles/Process.css";

const useVisible = (threshold = 0.1) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const STEPS = [
  {
    num: "01",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
      </svg>
    ),
    title: "Discovery & Strategy",
    desc: "We analyse your business goals, target audience, and competitive landscape to define a precise digital strategy and project roadmap.",
    deliverable: "Project Brief + Roadmap",
  },
  {
    num: "02",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
      </svg>
    ),
    title: "UI/UX Design",
    desc: "Modern, clean wireframes and high-fidelity mockups that reflect your brand identity — designed for usability, trust, and conversion.",
    deliverable: "Figma Designs + Prototype",
  },
  {
    num: "03",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
    title: "Development",
    desc: "Production-grade code built with modern frameworks, clean architecture, and best practices — responsive, fast, and built to scale.",
    deliverable: "Tested Codebase + Staging",
  },
  {
    num: "04",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.89 10a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.82 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 7 7l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
    title: "Review & Feedback",
    desc: "You review the live staging environment and we refine until every detail is exactly right — no arbitrary limits on revision cycles.",
    deliverable: "Client Approval",
  },
  {
    num: "05",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/>
      </svg>
    ),
    title: "Launch & Optimise",
    desc: "Your product goes live with SEO readiness, performance benchmarks, analytics setup, and a full handover — plus ongoing support.",
    deliverable: "Live Product + Handover",
  },
];

function Process() {
  const [headerRef, headerVis] = useVisible(0.1);
  const [stepsRef, stepsVis] = useVisible(0.06);

  return (
    <section className="process-section">
      <div className="about-rule" aria-hidden="true" />
      <div className="process-container">

        {/* Header */}
        <div ref={headerRef} className={`process-header ${headerVis ? "process-header--in" : ""}`}>
          <span className="cdx-label">// 03 — our process</span>
          <h2 className="cdx-title">
            &lt;<span className="orange"> How</span> codix Works<span className="orange">/</span>&gt;
          </h2>
          <div className="cdx-rule" />
          <p className="process-subtitle">
            A transparent, structured workflow that keeps you informed at every stage —
            from idea to a live product that performs.
          </p>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="process-steps">
          {STEPS.map((step, i) => (
            <div
              key={step.num}
              className={`process-step ${stepsVis ? "process-step--in" : ""}`}
              style={{ transitionDelay: `${i * 110}ms` }}
            >
              {/* connector line (between cards) */}
              {i < STEPS.length - 1 && (
                <div className="process-connector" aria-hidden="true" />
              )}

              <div className="step-num-wrap">
                <span className="step-num">{step.num}</span>
              </div>

              <div className="step-icon">{step.icon}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-desc">{step.desc}</p>

              <div className="step-deliverable">
                <span className="step-deliverable-label">Deliverable:</span>
                <span className="step-deliverable-value">{step.deliverable}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Process;
